import {
	ApiError,
	type ApiKey,
	type ApiKeyStatus,
	type IssuedApiKey,
	type PortalSettings,
} from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { formatDate } from "@khazzin/i18n/dates";
import { EmptyState, ErrorState, KeyValueList, PageHeader } from "@khazzin/ui/patterns/page";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { DataTable } from "@khazzin/ui/patterns/table";
import {
	Button,
	Checkbox,
	ConfirmDialog,
	Dialog,
	Field,
	Input,
	LinkTabs,
	RadioGroup,
	SkeletonText,
	Switch,
} from "@khazzin/ui/primitives";
import type { Tone } from "@khazzin/ui/styles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { NotFound } from "../../app/shell/Shell";
import { useStoreScope } from "../../app/shell/scope";
import { levelOf, reaches } from "../../app/shell/sections";
import { useServiceOff } from "../../app/shell/serviceSwitches";
import { OpenTicketDrawer } from "../tickets/OpenTicketDrawer";
import { NotificationSettings } from "./NotificationSettingsPage";
import { PackagingTab } from "./PackagingTab";
import { ServicesTab } from "./ServicesTab";

/**
 * The Store Portal's Settings (EP10-S11, DA-10-13, DA-10-14): Profile, Order
 * flow, Notifications, Preferences and API keys, one route each.
 *
 * Profile is the store record, read-only. Order flow is the store's own
 * switches — customer confirmation and auto-import per connected channel —
 * saved on each flip under the version read. At `settings = read` every
 * switch is disabled. API keys is the Integrations section's: shown only
 * when the grid shows it, the write controls only at `write`.
 */

export const SETTINGS_TABS = [
	"profile",
	"order-flow",
	"services",
	"packaging",
	"notifications",
	"preferences",
	"api-keys",
] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];

export function portalSettingsKey(storeId: string) {
	return ["portal", storeId, "settings"] as const;
}

const KEY_TONE: Record<ApiKeyStatus, Tone> = {
	active: "success",
	revoked: "danger",
	expired: "neutral",
};

/** The server's message for a refused write, or a generic one. */
function refusal(error: unknown, t: Translate): string {
	return error instanceof ApiError ? error.message : t("portal.settings.failed");
}

export function SettingsPage() {
	const { t } = useLocale();
	const { store } = useStoreScope();
	const params = useParams();
	const settings = levelOf(store, "settings");
	const keys = levelOf(store, "integrations");
	// Call note 2: Packaging leaves with the service the store switched off.
	const serviceOff = useServiceOff(store?.storeId ?? "");
	const tabs = SETTINGS_TABS.filter((tab) =>
		tab === "api-keys"
			? keys !== "none"
			: settings !== "none" && !(tab === "packaging" && serviceOff("PACKAGING")),
	);
	const tab = tabs.find((value) => value === params.tab) ?? tabs[0];
	const storeId = store?.storeId ?? "";

	if (tab === undefined || (params.tab !== undefined && params.tab !== tab)) {
		return <NotFound />;
	}

	return (
		<div className="flex flex-col gap-4">
			<PageHeader
				title={t("portal.shell.sections.settings")}
				subtitle={t("portal.settings.subtitle")}
			/>
			<LinkTabs
				aria-label={t("portal.settings.tabs.label")}
				tabs={tabs.map((value) => ({
					to: value === tabs[0] ? "/settings" : `/settings/${value}`,
					label: t(`portal.settings.tabs.${value}`),
					end: true,
				}))}
			/>
			{tab === "profile" && <ProfileTab storeId={storeId} />}
			{tab === "order-flow" && (
				<OrderFlowTab storeId={storeId} writable={reaches(settings, "write")} />
			)}
			{tab === "services" && (
				<ServicesTab storeId={storeId} writable={reaches(settings, "write")} />
			)}
			{tab === "packaging" && (
				<PackagingTab storeId={storeId} writable={reaches(settings, "write")} />
			)}
			{tab === "notifications" && <NotificationSettings />}
			{tab === "preferences" && <PreferencesTab />}
			{tab === "api-keys" && <ApiKeysTab storeId={storeId} writable={reaches(keys, "write")} />}
		</div>
	);
}

function useSettings(storeId: string) {
	const api = useApi();
	return useQuery({
		queryKey: portalSettingsKey(storeId),
		queryFn: ({ signal }) => api.getPortalSettings({ signal }),
		enabled: storeId !== "",
	});
}

/**
 * The settings while they load, or why they did not (ADJ-M03-4): a skeleton
 * rather than a blank tab, and a load failure that says so with Try again —
 * not "Could not save", which is the save's message.
 */
function SettingsPending({ query }: { query: ReturnType<typeof useSettings> }) {
	const { t } = useLocale();
	return query.isError ? (
		<ErrorState error={query.error} onRetry={() => void query.refetch()} t={t} />
	) : (
		<SkeletonText lines={4} />
	);
}

function dash(value: string | undefined | null): string {
	return value === undefined || value === null || value === "" ? "—" : value;
}

function ProfileTab({ storeId }: { storeId: string }) {
	const { t } = useLocale();
	const query = useSettings(storeId);
	const [asking, setAsking] = useState(false);
	const p = query.data?.profile;
	if (p === undefined) {
		return <SettingsPending query={query} />;
	}
	return (
		<div className="grid gap-4 md:grid-cols-2">
			<section aria-label={t("portal.settings.profile.store")} className="card p-4">
				<KeyValueList
					rows={[
						{ label: t("portal.settings.profile.name"), value: p.name },
						...(p.homeBased === true
							? [{ label: t("portal.settings.profile.homeBased"), value: t("ui.yes") }]
							: [
									{ label: t("portal.settings.profile.legalName"), value: dash(p.legalName) },
									{
										label: t("portal.settings.profile.registration"),
										value: dash(p.registrationNumber),
										mono: true,
									},
								]),
						{ label: t("portal.settings.profile.code"), value: p.storeCode, mono: true },
						{ label: t("portal.settings.profile.currency"), value: dash(p.defaultCurrency) },
						{ label: t("portal.settings.profile.timezone"), value: dash(p.timezone) },
						{ label: t("portal.settings.profile.promise"), value: dash(p.promise) },
						{ label: t("portal.settings.profile.remittance"), value: dash(p.remittanceCadence) },
					]}
				/>
			</section>
			<section
				aria-label={t("portal.settings.profile.contacts")}
				className="flex flex-col gap-3 card p-4"
			>
				<h2 className="text-[14px] font-bold text-fg">{t("portal.settings.profile.contacts")}</h2>
				{p.contacts.map((c) => (
					<KeyValueList
						key={`${c.fullName}-${c.phone ?? ""}`}
						rows={[
							{
								label: t(
									c.isPrimary
										? "portal.settings.profile.primary"
										: "portal.settings.profile.contact",
								),
								value: `${c.fullName} · ${c.role}`,
							},
							{ label: t("portal.settings.profile.phone"), value: dash(c.phone), mono: true },
							{ label: t("portal.settings.profile.email"), value: dash(c.email) },
						]}
					/>
				))}
				<p className="text-[12.5px] text-fg-muted">{t("portal.settings.profile.changeHint")}</p>
				<div>
					<Button variant="secondary" size="sm" onClick={() => setAsking(true)}>
						{t("portal.settings.profile.requestChange")}
					</Button>
				</div>
			</section>
			{/* EP10-S12's drawer: a question ticket, prefilled. */}
			<OpenTicketDrawer
				open={asking}
				onOpenChange={setAsking}
				source="none"
				prefill={t("portal.settings.profile.requestPrefill")}
			/>
		</div>
	);
}

function OrderFlowTab({ storeId, writable }: { storeId: string; writable: boolean }) {
	const { t } = useLocale();
	const api = useApi();
	const queryClient = useQueryClient();
	const query = useSettings(storeId);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const s = query.data;

	async function save(next: Pick<PortalSettings, "requireCustomerConfirmation" | "autoImport">) {
		if (s === undefined) {
			return;
		}
		setSaving(true);
		try {
			const saved = await api.putPortalSettings(next, s.version);
			queryClient.setQueryData(portalSettingsKey(storeId), saved);
			setError(null);
		} catch (caught) {
			setError(refusal(caught, t));
			// A stale version: read the settings as they now stand.
			void query.refetch();
		} finally {
			setSaving(false);
		}
	}

	if (s === undefined) {
		return <SettingsPending query={query} />;
	}
	const disabled = !writable || saving;
	return (
		<section className="flex max-w-2xl flex-col gap-4 card p-4">
			<SwitchRow
				label={t("portal.settings.orderFlow.confirmation")}
				hint={t("portal.settings.orderFlow.confirmationHint")}
				checked={s.requireCustomerConfirmation}
				disabled={disabled}
				onChange={(on) => void save({ requireCustomerConfirmation: on, autoImport: s.autoImport })}
			/>
			<h2 className="text-[14px] font-bold text-fg">{t("portal.settings.orderFlow.autoImport")}</h2>
			{s.autoImport.length === 0 ? (
				<p className="text-[12.5px] text-fg-muted">{t("portal.settings.orderFlow.noChannels")}</p>
			) : (
				s.autoImport.map((row) => (
					<SwitchRow
						key={row.channel}
						label={t(`portal.settings.orderFlow.channel.${row.channel}`)}
						hint={t("portal.settings.orderFlow.autoImportHint")}
						checked={row.enabled}
						disabled={disabled}
						onChange={(on) =>
							void save({
								requireCustomerConfirmation: s.requireCustomerConfirmation,
								autoImport: s.autoImport.map((r) =>
									r.channel === row.channel ? { ...r, enabled: on } : r,
								),
							})
						}
					/>
				))
			)}
			{error !== null && (
				<p role="alert" className="text-[12.5px] text-danger-fg">
					{error}
				</p>
			)}
		</section>
	);
}

function SwitchRow(props: {
	label: string;
	hint: string;
	checked: boolean;
	disabled: boolean;
	onChange: (on: boolean) => void;
}) {
	return (
		<div className="flex flex-col gap-1">
			<Switch
				label={props.label}
				checked={props.checked}
				disabled={props.disabled}
				onCheckedChange={props.onChange}
			/>
			<p className="text-[12.5px] text-fg-muted">{props.hint}</p>
		</div>
	);
}

/** The portal's own preference: its language, which this browser remembers. */
function PreferencesTab() {
	const { t, locale, switchLanguage } = useLocale();
	return (
		<section className="flex max-w-2xl flex-col gap-2 card p-4">
			<h2 className="text-[14px] font-bold text-fg">{t("portal.settings.preferences.language")}</h2>
			<RadioGroup
				aria-label={t("portal.settings.preferences.language")}
				value={locale}
				onValueChange={(value) => {
					if (value !== locale) {
						switchLanguage();
					}
				}}
				options={[
					{ value: "en", label: "English" },
					{ value: "ar", label: "العربية" },
				]}
			/>
		</section>
	);
}

function portalKeysKey(storeId: string) {
	return ["portal", storeId, "api-keys"] as const;
}

function ApiKeysTab({ storeId, writable }: { storeId: string; writable: boolean }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const queryClient = useQueryClient();
	const query = useQuery({
		queryKey: portalKeysKey(storeId),
		queryFn: ({ signal }) => api.listPortalApiKeys({ signal }),
		enabled: storeId !== "",
	});
	const [creating, setCreating] = useState(false);
	const [issued, setIssued] = useState<IssuedApiKey | null>(null);
	const [revoking, setRevoking] = useState<ApiKey | null>(null);
	const refresh = () => void queryClient.invalidateQueries({ queryKey: portalKeysKey(storeId) });
	const when = (at: string | undefined) => (at ? formatDate(at, { locale, timeZone: "UTC" }) : "—");
	const items = query.data ?? [];

	return (
		<div className="flex flex-col gap-3">
			{writable && (
				<div>
					<Button variant="primary" onClick={() => setCreating(true)}>
						{t("portal.apiKeys.create")}
					</Button>
				</div>
			)}
			<DataTable<ApiKey>
				screenKey="portal.apiKeys"
				caption={t("portal.settings.tabs.api-keys")}
				t={t}
				columns={[
					{ id: "name", headerKey: "portal.apiKeys.columns.name", cell: (k) => k.label },
					{
						id: "prefix",
						headerKey: "portal.apiKeys.columns.prefix",
						mono: true,
						cell: (k) => `khz_${k.mode}_…${k.prefixHint}`,
					},
					{
						id: "status",
						headerKey: "portal.apiKeys.columns.status",
						cell: (k) => (
							<StatusChip tone={KEY_TONE[k.status]}>
								{t(`portal.apiKeys.status.${k.status}`)}
							</StatusChip>
						),
					},
					{
						id: "created",
						headerKey: "portal.apiKeys.columns.created",
						cell: (k) => when(k.createdAt),
					},
					{
						id: "lastUsed",
						headerKey: "portal.apiKeys.columns.lastUsed",
						cell: (k) => when(k.lastUsedAt),
					},
					...(writable
						? [
								{
									id: "actions",
									headerKey: "portal.apiKeys.columns.actions",
									cell: (k: ApiKey) =>
										k.status === "active" ? (
											<Button size="sm" variant="ghost" onClick={() => setRevoking(k)}>
												{t("portal.apiKeys.revoke")}
											</Button>
										) : null,
								},
							]
						: []),
				]}
				rowKey={(k) => k.id}
				page={{ items, nextCursor: null }}
				status={query.isError ? "error" : query.data ? "success" : "loading"}
				error={query.error}
				onRetry={() => void query.refetch()}
				collapse="cards"
				pagination={{
					showingFrom: items.length > 0 ? 1 : 0,
					showingTo: items.length,
					total: items.length,
					hasNext: false,
					hasPrevious: false,
					onNext: () => {},
					onPrevious: () => {},
				}}
				empty={
					<EmptyState title={t("portal.apiKeys.emptyTitle")} body={t("portal.apiKeys.emptyBody")} />
				}
			/>
			{creating && (
				<CreateKeyDialog
					onClose={() => setCreating(false)}
					onIssued={(key) => {
						setCreating(false);
						setIssued(key);
						refresh();
					}}
				/>
			)}
			{issued !== null && <SecretDialog secret={issued.secret} onClose={() => setIssued(null)} />}
			<ConfirmDialog
				open={revoking !== null}
				onOpenChange={(open) => {
					if (!open) {
						setRevoking(null);
					}
				}}
				tone="danger"
				title={t("portal.apiKeys.revokeTitle", { name: revoking?.label ?? "" })}
				description={t("portal.apiKeys.revokeBody")}
				confirmLabel={t("portal.apiKeys.revoke")}
				cancelLabel={t("portal.settings.cancel")}
				onConfirm={async () => {
					if (revoking !== null) {
						await api.revokePortalApiKey(revoking.id);
						refresh();
					}
				}}
			/>
		</div>
	);
}

function CreateKeyDialog({
	onClose,
	onIssued,
}: {
	onClose: () => void;
	onIssued: (key: IssuedApiKey) => void;
}) {
	const { t } = useLocale();
	const api = useApi();
	const [label, setLabel] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	async function create() {
		setSaving(true);
		try {
			onIssued(await api.createPortalApiKey(label.trim()));
		} catch (caught) {
			setError(refusal(caught, t));
		} finally {
			setSaving(false);
		}
	}

	return (
		<Dialog
			open
			onOpenChange={(open) => {
				if (!open) {
					onClose();
				}
			}}
			title={t("portal.apiKeys.createTitle")}
			footer={
				<>
					<Button variant="secondary" onClick={onClose}>
						{t("portal.settings.cancel")}
					</Button>
					<Button
						variant="primary"
						disabled={saving || label.trim() === ""}
						onClick={() => void create()}
					>
						{t("portal.apiKeys.create")}
					</Button>
				</>
			}
		>
			<Field label={t("portal.apiKeys.name")} error={error ?? undefined}>
				{(control) => (
					<Input
						{...control}
						value={label}
						maxLength={100}
						onChange={(event) => setLabel(event.target.value)}
					/>
				)}
			</Field>
		</Dialog>
	);
}

/**
 * The one time the secret is on screen: Copy, and "I have stored it" before
 * Close enables. Closing drops it; nothing can fetch it again.
 */
export function SecretDialog({ secret, onClose }: { secret: string; onClose: () => void }) {
	const { t } = useLocale();
	const [stored, setStored] = useState(false);
	const [copied, setCopied] = useState(false);
	return (
		<Dialog
			open
			onOpenChange={(open) => {
				if (!open && stored) {
					onClose();
				}
			}}
			title={t("portal.apiKeys.secretTitle")}
			description={t("portal.apiKeys.secretBody")}
			footer={
				<Button variant="primary" disabled={!stored} onClick={onClose}>
					{t("portal.apiKeys.close")}
				</Button>
			}
		>
			<div className="flex flex-col gap-3">
				<Input
					readOnly
					aria-label={t("portal.apiKeys.secretLabel")}
					className="font-mono"
					value={secret}
					onFocus={(event) => event.currentTarget.select()}
				/>
				<div>
					<Button
						variant="secondary"
						size="sm"
						onClick={() =>
							void navigator.clipboard.writeText(secret).then(
								() => setCopied(true),
								() => setCopied(false),
							)
						}
					>
						{t(copied ? "portal.apiKeys.copied" : "portal.apiKeys.copy")}
					</Button>
				</div>
				<Checkbox
					checked={stored}
					onCheckedChange={(checked) => setStored(checked === true)}
					label={t("portal.apiKeys.stored")}
				/>
			</div>
		</Dialog>
	);
}
